import { useState } from "react";
import { ApiError } from "../../api";
import {
  useAdminApiScopes,
  useAdminOrgs,
  useCreateAdminApiKey,
} from "../../hooks/queries";
import type { AdminApiScope, AdminOrgRow } from "../../types/contract";

export function apiMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return "Request failed";
}

function nameCounts(orgs: AdminOrgRow[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const org of orgs) {
    const name = (org.name ?? "").toLowerCase();
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return counts;
}

function orgLabel(org: AdminOrgRow, counts: Map<string, number>): string {
  const name = org.name ?? org.org_id;
  if ((counts.get(name.toLowerCase()) ?? 0) < 2) return name;
  const fields = org.field_count ?? 0;
  return `${name} · ${fields} fields · …${org.org_id.slice(-6)}`;
}

export function toggleScope(list: string[], scope: string): string[] {
  return list.includes(scope)
    ? list.filter((item) => item !== scope)
    : [...list, scope];
}

export function OrgPicker({
  enabled,
  exclude,
  onPick,
}: {
  enabled: boolean;
  exclude: string[];
  onPick: (orgId: string) => void;
}) {
  const [q, setQ] = useState("");
  // The filter must span EVERY org, including ones this admin doesn't belong to -- `orgStore` holds only
  // their own memberships, so it can't serve a system view.
  const adminOrgs = useAdminOrgs(enabled);
  const all = Array.isArray(adminOrgs.data) ? adminOrgs.data : [];
  const counts = nameCounts(all);
  const needle = q.trim().toLowerCase();
  const linked = new Set(exclude);
  const choices = all.filter(
    (org) =>
      !linked.has(org.org_id) &&
      (org.name ?? "").toLowerCase().includes(needle),
  );
  return (
    <div className="mt-1">
      <input
        value={q}
        onChange={(event) => setQ(event.target.value)}
        placeholder="Find an organization…"
        aria-label="Find an organization"
        className="w-full rounded border border-line bg-canvas px-1.5 py-1 text-[11px] outline-none"
      />
      <ul className="mt-1 max-h-28 overflow-auto">
        {choices.map((org) => (
          <li key={org.org_id}>
            <button
              type="button"
              className="block w-full truncate px-1 py-0.5 text-left text-[11px] hover:bg-hover"
              onClick={() => onPick(org.org_id)}
            >
              {orgLabel(org, counts)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ScopeChecks({
  scopes,
  selected,
  onToggle,
}: {
  scopes: AdminApiScope[];
  selected: string[];
  onToggle: (scope: string) => void;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {scopes.map((item) => (
        <label
          key={item.scope}
          className="flex items-center gap-1 text-[11px]"
          title={item.description}
        >
          <input
            type="checkbox"
            checked={selected.includes(item.scope)}
            onChange={() => onToggle(item.scope)}
          />
          {item.scope}
        </label>
      ))}
    </div>
  );
}

function CreateFields(props: {
  name: string;
  cap: number;
  orgIds: string[];
  chosen: string[];
  catalog: AdminApiScope[];
  enabled: boolean;
  onName: (value: string) => void;
  onCap: (value: number) => void;
  onOrg: (orgId: string) => void;
  onRemoveOrg: (orgId: string) => void;
  onToggle: (scope: string) => void;
}) {
  const adminOrgs = useAdminOrgs(props.enabled);
  const known = Array.isArray(adminOrgs.data) ? adminOrgs.data : [];
  return (
    <>
      <input
        aria-label="Key label"
        value={props.name}
        onChange={(event) => props.onName(event.target.value)}
        required
        className="rounded border border-line bg-canvas px-1.5 py-1 text-[11px]"
        placeholder="Label"
      />
      <input
        aria-label="Orders per day"
        type="number"
        min={1}
        value={props.cap}
        onChange={(event) => props.onCap(Number(event.target.value))}
        className="w-24 rounded border border-line bg-canvas px-1.5 py-1 text-[11px]"
      />
      <div className="flex flex-wrap gap-1">
        {props.orgIds.map((orgId) => {
          const name = known.find((org) => org.org_id === orgId)?.name || orgId;
          return (
            <span
              key={orgId}
              className="inline-flex items-center gap-1 rounded border border-line px-1"
            >
              {name}
              <button
                type="button"
                aria-label={`Remove ${name}`}
                onClick={() => props.onRemoveOrg(orgId)}
              >
                ×
              </button>
            </span>
          );
        })}
      </div>
      <OrgPicker
        enabled={props.enabled}
        exclude={props.orgIds}
        onPick={props.onOrg}
      />
      <ScopeChecks
        scopes={props.catalog}
        selected={props.chosen}
        onToggle={props.onToggle}
      />
    </>
  );
}

export function CreateKeyForm({
  enabled,
  onClose,
  onIssued,
}: {
  enabled: boolean;
  onClose: () => void;
  onIssued: (token: string, prefix: string) => void;
}) {
  const scopes = useAdminApiScopes(enabled);
  const create = useCreateAdminApiKey();
  const [name, setName] = useState("");
  const [cap, setCap] = useState(100);
  const [orgIds, setOrgIds] = useState<string[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const catalog = Array.isArray(scopes.data) ? scopes.data : [];
  return (
    <form
      className="mb-2 flex flex-col gap-2 rounded border border-line p-2"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        create.mutate(
          { name, scopes: chosen, org_ids: orgIds, max_orders_per_day: cap },
          {
            onSuccess: (issued) => {
              onIssued(issued.token, issued.prefix);
              create.reset();
            },
            onError: (err) => setError(apiMessage(err)),
          },
        );
      }}
    >
      <CreateFields
        name={name}
        cap={cap}
        orgIds={orgIds}
        chosen={chosen}
        catalog={catalog}
        enabled={enabled}
        onName={setName}
        onCap={setCap}
        onOrg={(orgId) => setOrgIds((ids) => [...ids, orgId])}
        onRemoveOrg={(orgId) =>
          setOrgIds((ids) => ids.filter((id) => id !== orgId))
        }
        onToggle={(scope) => setChosen((cur) => toggleScope(cur, scope))}
      />
      {error && <p className="text-[11px] text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={create.isPending || !name.trim()}
          className="rounded border border-line px-2 py-1 text-[11px] hover:bg-hover disabled:opacity-50"
        >
          Create key
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-muted"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
